import { useEffect } from 'react';

export interface TableKeyboardShortcutsConfig<T = any> {
  onDelete?: (item: T) => void;
  onCreate?: () => void;
  selectedRow?: T | null;
  selectedRows?: string[];
  onBulkDelete?: (selectedIds: string[]) => void;
  enabled?: boolean;
}

/**
 * Custom hook for handling keyboard shortcuts in data tables
 * 
 * Shortcuts:
 * - D: Delete selected row(s) - works with checked rows or currently selected row
 * - N: Create new entry
 * 
 * Usage Example:
 * ```tsx
 * const [selectedRowId, setSelectedRowId] = useState<string>();
 * const [selectedRows, setSelectedRows] = useState<string[]>([]);
 * const selectedItem = items.find(item => item.id === selectedRowId);
 * 
 * useTableKeyboardShortcuts({
 *   onDelete: handleDelete,
 *   onBulkDelete: handleBulkDelete,
 *   onCreate: handleCreate,
 *   selectedRow: selectedItem,
 *   selectedRows: selectedRows,
 *   enabled: !isModalOpen
 * });
 * 
 * // In DataTable component:
 * <DataTable
 *   onRowClick={(item) => setSelectedRowId(item.id)}
 *   onSelectionChange={setSelectedRows}
 *   selectedRowId={selectedRowId}
 *   ...
 * />
 * ```
 * 
 * @param config Configuration object with handlers and selected row
 */
export const useTableKeyboardShortcuts = <T extends any>({
  onDelete,
  onCreate,
  selectedRow,
  selectedRows = [],
  onBulkDelete,
  enabled = true
}: TableKeyboardShortcutsConfig<T>) => {
  useEffect(() => {
    if (!enabled) return;

    const handleKeyPress = (event: KeyboardEvent) => {
      // Ignore if user is typing in an input, textarea, or contenteditable element
      // But allow checkboxes to trigger delete (they're INPUT but we want D key to work)
      const target = event.target as HTMLElement;
      const isCheckbox = target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'checkbox';
      
      // Allow checkboxes to work, but ignore other input types
      if (target.tagName === 'INPUT' && !isCheckbox) {
        return;
      }
      if (target.tagName === 'TEXTAREA') {
        return;
      }
      if (target.isContentEditable && !isCheckbox) {
        return;
      }

      const key = event.key.toLowerCase();

      switch (key) {
        case 'd':
          // Prioritize bulk delete if rows are checked
          if (selectedRows && selectedRows.length > 0 && onBulkDelete) {
            event.preventDefault();
            onBulkDelete(selectedRows);
          } else if (selectedRow && onDelete) {
            event.preventDefault();
            onDelete(selectedRow);
          }
          break;

        case 'n':
          if (onCreate) {
            event.preventDefault();
            onCreate();
          }
          break;

        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [onDelete, onCreate, selectedRow, selectedRows, onBulkDelete, enabled]);
};

